import apiClient from './client';
import type {
  DataLibraryParticipant,
  DataLibraryCohortGroup,
  DataLibraryFilterOptions,
  DataLibrarySurveyRow,
  DataLibrarySurveyQuestion,
  DataLibrarySurveyFilterOptions,
  DataLibraryCoachSurveyRow,
  DataLibraryCoachSurveyFilterOptions,
  DataLibraryAttendanceRow,
  DataLibraryAttendanceFilterOptions,
  DataLibrarySessionFeedbackRow,
  DataLibrarySessionFeedbackFilterOptions,
  DataLibrarySessionRow,
  DataLibrarySessionFilterOptions,
  DataLibraryCoachPostSessionRow,
  DataLibraryCoachPostSessionFilterOptions,
  DataLibraryOneOnOneRow,
  DataLibraryOneOnOneFilterOptions,
  DataLibraryOneOnOneSummary,
  ColumnAverages,
} from '../../types';

// ============================================================================
// Response Types
// ============================================================================

export interface DataLibraryParticipantResponse {
  data: DataLibraryParticipant[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibraryFilterOptions;
  };
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface DataLibrarySurveyResponseResponse {
  data: DataLibrarySurveyRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibrarySurveyFilterOptions;
    questions: DataLibrarySurveyQuestion[];
    hasSurveyConfig: boolean;
  };
}

export interface DataLibraryCoachSurveyResponseResponse {
  data: DataLibraryCoachSurveyRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibraryCoachSurveyFilterOptions;
    questions: DataLibrarySurveyQuestion[];
    hasSurveyConfig: boolean;
  };
}

export interface DataLibraryAttendanceResponse {
  data: DataLibraryAttendanceRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibraryAttendanceFilterOptions;
  };
}

export interface DataLibrarySessionSummaryResponse {
  data: DataLibrarySessionRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibrarySessionFilterOptions;
  };
}

export interface DataLibrarySessionFeedbackResponseType {
  data: DataLibrarySessionFeedbackRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibrarySessionFeedbackFilterOptions;
    questions: DataLibrarySurveyQuestion[];
    hasSurveyConfig: boolean;
  };
}

export interface DataLibrarySessionFeedbackParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  sessionNumber?: string;
  flaggedOnly?: boolean;
  hasAbsences?: boolean;
}

/** MED-668: Coach Post Session response */
export interface DataLibraryCoachPostSessionResponseType {
  data: DataLibraryCoachPostSessionRow[];
  meta: {
    /** MDF-41: average per numeric column id over all matching rows */
    averages: ColumnAverages;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filterOptions: DataLibraryCoachPostSessionFilterOptions;
    questions: DataLibrarySurveyQuestion[];
    hasSurveyConfig: boolean;
  };
}

/** MED-668: Coach Post Session params */
export interface DataLibraryCoachPostSessionParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  sessionNumber?: string;
  missingFeedback?: boolean;
  hasFlags?: boolean;
}

export interface DataLibraryOneOnOneParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  cohortId?: string;
  coachId?: string;
  /** Comma-separated OneOnOneReportStatus values */
  status?: string;
  /** Comma-separated one_on_one_type values */
  type?: string;
}

export interface DataLibraryAttendanceParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  status?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  sessionNumber?: string;
  attendanceStatus?: string;
  department?: string;
  location?: string;
  businessUnit?: string;
  absentOnly?: boolean;
  unexcusedOnly?: boolean;
}

export interface DataLibraryCoachSurveyParams {
  cohortIds: string[];
  surveyType: 'midpoint' | 'endpoint';
  /** MED-668: Multiple survey types for mid-vs-end comparison */
  surveyTypes?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
}

export interface DataLibrarySurveyParams {
  cohortIds: string[];
  formTypes: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  flaggedOnly?: boolean;
  hasAbsences?: boolean;
  excludePartnerCoaches?: boolean;
}

export interface DataLibrarySessionSummaryParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  cohortId?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  sessionMonth?: string;
  sessionNumber?: string;
  sessionStatus?: 'completed' | 'upcoming' | 'flagged';
}

export interface DataLibraryParticipantParams {
  cohortIds: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  status?: string;
  /** MDF-211: one group id or a comma-separated list */
  groupId?: string;
  coachId?: string;
  department?: string;
  location?: string;
  flaggedOnly?: boolean;
  hasAbsences?: boolean;
  midpointCompleted?: boolean;
  endpointCompleted?: boolean;
}

// ============================================================================
// API Methods
// ============================================================================

export const dataLibraryApi = {
  /**
   * Get participants across multiple cohorts with enriched data
   */
  getParticipants: async (
    params: DataLibraryParticipantParams,
  ): Promise<DataLibraryParticipantResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.department) queryParams.set('department', params.department);
    if (params.location) queryParams.set('location', params.location);
    if (params.flaggedOnly) queryParams.set('flaggedOnly', 'true');
    if (params.hasAbsences) queryParams.set('hasAbsences', 'true');
    if (params.midpointCompleted !== undefined)
      queryParams.set('midpointCompleted', String(params.midpointCompleted));
    if (params.endpointCompleted !== undefined)
      queryParams.set('endpointCompleted', String(params.endpointCompleted));

    const result = await apiClient.rawGetWithMeta<DataLibraryParticipant[]>(
      `/api/data-library/participants?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibraryParticipantResponse['meta']['pagination']) || {
          page: 1,
          limit: 25,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibraryFilterOptions) || {
          groups: [],
          coaches: [],
          departments: [],
          locations: [],
        },
      },
    };
  },

  /**
   * Get per-participant survey responses across cohorts
   */
  getSurveyResponses: async (
    params: DataLibrarySurveyParams,
  ): Promise<DataLibrarySurveyResponseResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    queryParams.set('formTypes', params.formTypes.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.flaggedOnly) queryParams.set('flaggedOnly', 'true');
    if (params.hasAbsences) queryParams.set('hasAbsences', 'true');
    if (params.excludePartnerCoaches) queryParams.set('excludePartnerCoaches', 'true');

    const result = await apiClient.rawGetWithMeta<DataLibrarySurveyRow[]>(
      `/api/data-library/survey-responses?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibrarySurveyResponseResponse['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibrarySurveyFilterOptions) || {
          groups: [],
          coaches: [],
        },
        questions: (result.meta?.questions as DataLibrarySurveyQuestion[]) || [],
        hasSurveyConfig: (result.meta?.hasSurveyConfig as boolean) ?? false,
      },
    };
  },

  /**
   * Get per-coach survey responses across cohorts
   */
  getCoachSurveyResponses: async (
    params: DataLibraryCoachSurveyParams,
  ): Promise<DataLibraryCoachSurveyResponseResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.surveyTypes && params.surveyTypes.length > 0) {
      queryParams.set('surveyTypes', params.surveyTypes.join(','));
    } else {
      queryParams.set('surveyType', params.surveyType);
    }
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);

    const result = await apiClient.rawGetWithMeta<DataLibraryCoachSurveyRow[]>(
      `/api/data-library/coach-survey-responses?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibraryCoachSurveyResponseResponse['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibraryCoachSurveyFilterOptions) || {
          cohorts: [],
        },
        questions: (result.meta?.questions as DataLibrarySurveyQuestion[]) || [],
        hasSurveyConfig: (result.meta?.hasSurveyConfig as boolean) ?? false,
      },
    };
  },

  /**
   * Get attendance data across multiple cohorts (session × participant grain)
   */
  getAttendance: async (
    params: DataLibraryAttendanceParams,
  ): Promise<DataLibraryAttendanceResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.sessionNumber) queryParams.set('sessionNumber', params.sessionNumber);
    if (params.attendanceStatus) queryParams.set('attendanceStatus', params.attendanceStatus);
    if (params.department) queryParams.set('department', params.department);
    if (params.location) queryParams.set('location', params.location);
    if (params.businessUnit) queryParams.set('businessUnit', params.businessUnit);
    if (params.absentOnly) queryParams.set('absentOnly', 'true');
    if (params.unexcusedOnly) queryParams.set('unexcusedOnly', 'true');

    const result = await apiClient.rawGetWithMeta<DataLibraryAttendanceRow[]>(
      `/api/data-library/attendance?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibraryAttendanceResponse['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibraryAttendanceFilterOptions) || {
          groups: [],
          coaches: [],
          sessionNumbers: [],
          departments: [],
          locations: [],
          businessUnits: [],
        },
      },
    };
  },

  /**
   * Get per-participant post-session feedback across cohorts
   */
  getSessionFeedback: async (
    params: DataLibrarySessionFeedbackParams,
  ): Promise<DataLibrarySessionFeedbackResponseType> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.sessionNumber) queryParams.set('sessionNumber', params.sessionNumber);
    if (params.flaggedOnly) queryParams.set('flaggedOnly', 'true');
    if (params.hasAbsences) queryParams.set('hasAbsences', 'true');

    const result = await apiClient.rawGetWithMeta<DataLibrarySessionFeedbackRow[]>(
      `/api/data-library/session-feedback?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibrarySessionFeedbackResponseType['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibrarySessionFeedbackFilterOptions) || {
          groups: [],
          coaches: [],
          sessionNumbers: [],
        },
        questions: (result.meta?.questions as DataLibrarySurveyQuestion[]) || [],
        hasSurveyConfig: (result.meta?.hasSurveyConfig as boolean) ?? false,
      },
    };
  },

  /**
   * Get session summary data across multiple cohorts (one row per session)
   */
  getSessions: async (
    params: DataLibrarySessionSummaryParams,
  ): Promise<DataLibrarySessionSummaryResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.cohortId) queryParams.set('cohortId', params.cohortId);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.sessionMonth) queryParams.set('sessionMonth', params.sessionMonth);
    if (params.sessionNumber) queryParams.set('sessionNumber', params.sessionNumber);
    if (params.sessionStatus) queryParams.set('sessionStatus', params.sessionStatus);

    const result = await apiClient.rawGetWithMeta<DataLibrarySessionRow[]>(
      `/api/data-library/sessions?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibrarySessionSummaryResponse['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibrarySessionFilterOptions) || {
          groups: [],
          cohorts: [],
          coaches: [],
          sessionMonths: [],
          sessionNumbers: [],
        },
      },
    };
  },

  /**
   * MED-668: Get coach post-session feedback with attendance, engagement, and flags
   */
  getCoachPostSessionFeedback: async (
    params: DataLibraryCoachPostSessionParams,
  ): Promise<DataLibraryCoachPostSessionResponseType> => {
    const queryParams = new URLSearchParams();
    queryParams.set('cohortIds', params.cohortIds.join(','));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.search) queryParams.set('search', params.search);
    if (params.groupId) queryParams.set('groupId', params.groupId);
    if (params.coachId) queryParams.set('coachId', params.coachId);
    if (params.sessionNumber) queryParams.set('sessionNumber', params.sessionNumber);
    if (params.missingFeedback) queryParams.set('missingFeedback', 'true');
    if (params.hasFlags) queryParams.set('hasFlags', 'true');

    const result = await apiClient.rawGetWithMeta<DataLibraryCoachPostSessionRow[]>(
      `/api/data-library/coach-post-session?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      meta: {
        averages: (result.meta?.averages as ColumnAverages) || {},
        pagination: (result.meta?.pagination as DataLibraryCoachPostSessionResponseType['meta']['pagination']) || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        },
        filterOptions: (result.meta?.filterOptions as DataLibraryCoachPostSessionFilterOptions) || {
          groups: [],
          cohorts: [],
          coaches: [],
          sessionNumbers: [],
        },
        questions: (result.meta?.questions as DataLibrarySurveyQuestion[]) || [],
        hasSurveyConfig: (result.meta?.hasSurveyConfig as boolean) ?? false,
      },
    };
  },

  /**
   * MDF-253: one row per 1:1 session, plus a status summary
   */
  getOneOnOnes: async (params: DataLibraryOneOnOneParams) => {
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === '') continue;
      queryParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
    const result = await apiClient.rawGetWithMeta<DataLibraryOneOnOneRow[]>(
      `/api/data-library/one-on-ones?${queryParams.toString()}`,
    );
    return {
      data: result.data,
      pagination: result.meta?.pagination as { total: number; totalPages: number },
      filterOptions: result.meta?.filterOptions as DataLibraryOneOnOneFilterOptions,
      summary: result.meta?.summary as DataLibraryOneOnOneSummary,
    };
  },

  /**
   * Get cohorts grouped by partner for the sidebar
   */
  getCohorts: async (): Promise<DataLibraryCohortGroup[]> => {
    return apiClient.get<DataLibraryCohortGroup[]>('/api/data-library/cohorts');
  },
};

export default dataLibraryApi;

// MDF-39: grouping holds every row in the browser, and above this the tab gets slow to load and scroll.
export const MAX_GROUPED_ROWS = 5000;

/**
 * MDF-39: a grouped table groups every matching row, not one page.
 * Fetches all pages at the API cap of 250 rows, 4 requests at a time, and joins the rows.
 * Stops early when isStale() returns true; the caller then drops the result.
 */
export async function fetchAllPages<
  R extends { data: unknown[]; meta: { pagination: { total: number; totalPages: number } } },
>(fetchPage: (page: number, limit: number) => Promise<R>, isStale?: () => boolean): Promise<R> {
  const limit = 250;
  const first = await fetchPage(1, limit);
  // Over the limit the caller shows the paged table, so the other pages are not needed.
  if (first.meta.pagination.total > MAX_GROUPED_ROWS) return first;
  const data = [...first.data];
  const totalPages = first.meta.pagination.totalPages;
  for (let start = 2; start <= totalPages; start += 4) {
    // A newer request replaces this one, so stop asking for its pages.
    if (isStale?.()) break;
    const pages = Array.from({ length: Math.min(4, totalPages - start + 1) }, (_, i) => start + i);
    const results = await Promise.all(pages.map(p => fetchPage(p, limit)));
    for (const r of results) data.push(...r.data);
  }
  return { ...first, data };
}
